import type { ChipTone } from '@mysaiva/core/components'
import type {
  ApiKeyEnvironment,
  ExpirationOption,
  ScopeDefinition,
  ScopeGroup,
  ScopeGroupId
} from './apiKeys.types'

export const SCOPE_GROUPS: ScopeGroup[] = [
  {
    id: 'patients',
    labelKey: 'developerPlatform.apiKeys.scopeGroups.patients',
    descriptionKey: 'developerPlatform.apiKeys.scopeGroupDescriptions.patients'
  },
  {
    id: 'clinical-data',
    labelKey: 'developerPlatform.apiKeys.scopeGroups.clinicalData',
    descriptionKey: 'developerPlatform.apiKeys.scopeGroupDescriptions.clinicalData'
  },
  {
    id: 'clinical-intelligence',
    labelKey: 'developerPlatform.apiKeys.scopeGroups.clinicalIntelligence',
    descriptionKey: 'developerPlatform.apiKeys.scopeGroupDescriptions.clinicalIntelligence'
  },
  {
    id: 'reports',
    labelKey: 'developerPlatform.apiKeys.scopeGroups.reports',
    descriptionKey: 'developerPlatform.apiKeys.scopeGroupDescriptions.reports'
  }
]

/**
 * Scope catalog of the SAIVA Intelligence API. The ids mirror the backend
 * `ApiScope` enum (`GET integrations/v1/orgs/{org_id}/scopes` serves the
 * same list with its own group labels); the grouping here is the UI's
 * until the pickers read the catalog from that endpoint.
 */
export const SCOPE_CATALOG: ScopeDefinition[] = [
  { id: 'patients.read', description: 'List & search patients', group: 'patients' },
  { id: 'patients.details.read', description: 'Patient summary & demographics', group: 'patients' },
  { id: 'patients.vitals.read', description: 'Vital-sign time series', group: 'clinical-data' },
  { id: 'patients.labs.read', description: 'Lab results', group: 'clinical-data' },
  { id: 'patients.diagnoses.read', description: 'Active diagnoses', group: 'clinical-data' },
  { id: 'patients.medications.read', description: 'Medication orders', group: 'clinical-data' },
  { id: 'patients.orders.read', description: 'Non-medication orders', group: 'clinical-data' },
  { id: 'patients.progress_notes.read', description: 'Progress notes', group: 'clinical-data' },
  {
    id: 'patients.risk_score.read',
    description: 'AI risk score & risk level',
    group: 'clinical-intelligence'
  },
  {
    id: 'patients.risk_trend.read',
    description: 'Daily risk score history',
    group: 'clinical-intelligence'
  },
  {
    id: 'patients.risk_conditions.read',
    description: 'Clinical risk-condition tags',
    group: 'clinical-intelligence'
  },
  {
    id: 'patients.risk_drivers.read',
    description: 'Top factors driving the risk score',
    group: 'clinical-intelligence'
  },
  {
    id: 'patients.care_actions.read',
    description: 'AI-suggested care actions',
    group: 'clinical-intelligence'
  },
  { id: 'patients.alerts.read', description: 'Patient alerts', group: 'clinical-intelligence' },
  {
    id: 'patients.event_trajectory.read',
    description: 'Clinical events by area over time',
    group: 'clinical-intelligence'
  },
  { id: 'reports.read', description: 'List available reports', group: 'reports' },
  { id: 'reports.export', description: 'Download report files', group: 'reports' },
  { id: 'reports.daily_risk.read', description: 'List daily risk reports', group: 'reports' },
  {
    id: 'reports.daily_risk.export',
    description: 'Daily risk report PDF & chart images',
    group: 'reports'
  },
  {
    id: 'reports.daily_risk.content.read',
    description: 'Daily risk report content as JSON',
    group: 'reports'
  }
]

export const SCOPE_GROUP_TONE: Record<ScopeGroupId, ChipTone> = {
  patients: 'primary',
  'clinical-data': 'info',
  'clinical-intelligence': 'purple',
  reports: 'teal'
}

export function getScope(id: string): ScopeDefinition | undefined {
  return SCOPE_CATALOG.find(scope => scope.id === id)
}

export function getScopeGroup(id: ScopeGroupId): ScopeGroup {
  return SCOPE_GROUPS.find(group => group.id === id) ?? SCOPE_GROUPS[0]
}

export const EXPIRATION_OPTIONS: ExpirationOption[] = ['30d', '3m', '6m', '1y', 'never']

export const ENVIRONMENTS: ApiKeyEnvironment[] = ['sandbox', 'production']

const DAY_SECONDS = 86_400

/**
 * Fixed lifetimes in days. The backend takes a duration in seconds, so the
 * presets are exact day counts rather than calendar months: calendar
 * arithmetic in local time would shift the lifetime by an hour across a
 * daylight-saving change.
 */
const EXPIRATION_DAYS: Record<Exclude<ExpirationOption, 'never'>, number> = {
  '30d': 30,
  '3m': 90,
  '6m': 180,
  '1y': 365
}

/** ISO expiry for an option, relative to `from` (defaults to now); `null` for `never`. */
export function expirationToDate(option: ExpirationOption, from: Date = new Date()): string | null {
  if (option === 'never') return null
  return new Date(from.getTime() + EXPIRATION_DAYS[option] * DAY_SECONDS * 1000).toISOString()
}

/**
 * Lifetime in whole seconds for an option, as the backend's
 * `expires_in_seconds` expects; `null` for `never`.
 *
 * The backend anchors the lifetime on the key's `issued_at` (now for a new
 * key, the existing term start for an update), so pass `anchor` when editing
 * an existing key: the result is the number of seconds from that anchor that
 * lands the expiry `option` away from `from`.
 */
export function expirationToSeconds(
  option: ExpirationOption,
  from: Date = new Date(),
  anchor: Date = from
): number | null {
  if (option === 'never') return null
  const expiresAt = from.getTime() + EXPIRATION_DAYS[option] * DAY_SECONDS * 1000
  return Math.max(1, Math.round((expiresAt - anchor.getTime()) / 1000))
}

/** Best-effort inverse of `expirationToDate`, used to pre-select the Edit panel. */
export function dateToExpiration(
  expiresAt: string | null,
  from: Date = new Date()
): ExpirationOption {
  if (!expiresAt) return 'never'
  const days = (new Date(expiresAt).getTime() - from.getTime()) / 86_400_000
  if (days <= 45) return '30d'
  if (days <= 135) return '3m'
  if (days <= 270) return '6m'
  return '1y'
}
